import React, { useCallback } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';


const SearchBar = ({
  searchQuery,
  onSearchChange,
  nameSearchEnabled,
  skuSearchEnabled,
  onNameSearchToggle,
  onSkuSearchToggle,
  onClearFilters,
  isSearching
}) => {
  // ✅ Enhanced search change handler with error recovery
  const handleInputChange = useCallback((e) => {
    try {
      const value = e?.target?.value || '';
      onSearchChange(value);
    } catch (error) {
      console.error('Error in search input change:', error);
      // Try to recover by clearing the search
      onSearchChange('');
    }
  }, [onSearchChange]);

  // ✅ Enhanced clear handler with confirmation for non-empty searches
  const handleClearClick = useCallback(() => {
    try {
      if (searchQuery && searchQuery?.length > 5) {
        const confirmed = window.confirm(
          `Vuoi davvero cancellare la ricerca "${searchQuery}"?`
        );
        if (!confirmed) {
          return;
        }
      }
      onClearFilters();
    } catch (error) {
      console.error('Error clearing filters:', error);
      // Force clear even if there's an error
      try {
        onSearchChange('');
      } catch (fallbackError) {
        console.error('Fallback clear also failed:', fallbackError);
      }
    }
  }, [searchQuery, onClearFilters, onSearchChange]);

  // ✅ Enhanced checkbox handlers with error recovery
  const handleNameToggle = useCallback((e) => {
    try {
      onNameSearchToggle(e?.target?.checked ?? true);
    } catch (error) {
      console.error('Error toggling name search:', error);
      onNameSearchToggle(true); // Default to enabled
    }
  }, [onNameSearchToggle]);

  const handleSkuToggle = useCallback((e) => {
    try {
      onSkuSearchToggle(e?.target?.checked ?? true);
    } catch (error) {
      console.error('Error toggling SKU search:', error);
      onSkuSearchToggle(true); // Default to enabled
    }
  }, [onSkuSearchToggle]);

  return (
    <div className="bg-card rounded-lg border border-border p-4 sm:p-6 shadow-subtle overflow-hidden">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
        {/* Search Input */}
        <div className="flex-1 w-full">
          <Input
            type="search"
            label="Cerca Prodotti"
            placeholder="Inserisci nome prodotto o codice SKU..."
            value={searchQuery || ''}
            onChange={handleInputChange}
            className="w-full"
            autoComplete="off"
            spellCheck="false"
          />
          {/* ✅ Add search status indicator */}
          {isSearching && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center">
              <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full mr-1"></span>
              Ricerca in corso...
            </p>
          )}
        </div>

        {/* Search Options */}
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center lg:w-auto">
          <div className="flex w-full flex-col gap-2 sm:w-auto">
            <Checkbox
              label="Ricerca per Nome"
              checked={nameSearchEnabled}
              onChange={handleNameToggle}
              size="sm"
            />
            <Checkbox
              label="Ricerca per SKU"
              checked={skuSearchEnabled}
              onChange={handleSkuToggle}
              size="sm"
            />
          </div>

          {/* Clear Button with enhanced visual feedback */}
          <Button
            variant="outline"
            onClick={handleClearClick}
            iconName="X"
            iconPosition="left"
            iconSize={16}
            disabled={isSearching}
            className={`w-full whitespace-nowrap transition-all duration-200 sm:w-auto ${
              searchQuery?.length > 0 
                ? 'border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400' :'border-gray-300 text-gray-500'
            }`}
            title={searchQuery?.length > 0 ? `Cancella ricerca: "${searchQuery}"` : 'Cancella filtri'}
          >
            {searchQuery?.length > 0 ? 'Cancella Ricerca' : 'Cancella Filtri'}
          </Button>
        </div>
      </div>
      
      {/* ✅ Add search tips */}
      {!searchQuery && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-700">
            💡 <strong>Suggerimenti di ricerca:</strong> Cerca per nome prodotto o codice SKU. 
            Usa spazi per cercare più parole. La ricerca è istantanea mentre digiti.
          </p>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
